import { Loadable } from '@/components/loaders';
import { RouteConstants } from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useWorkspaceAdmin } from '@/features/workspaces/hooks/useWorkspace';
import { PageLayout } from '@/global/components';
import { BreadcrumbItem, Breadcrumbs } from '@/lib/Breadcrumbs';
import { Button } from '@/lib/Buttons';
import { useGetDataSourcesByIdsQuery } from '@/services/dataSources';
import { useGetAudienceQuery } from '@/services/endpoints/audiences';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { AudienceDetailsDestinations } from './AudienceDetailsDestinations';
import { AudienceDetailsHeader } from './AudienceDetailsHeader';
import { AudienceDetailsRules } from './AudienceDetailsRules';
import { AudienceDetailsSources } from './AudienceDetailsSources';
import ErrorBoundary from '@/components/ErrorBoundary';

export const AudienceDetailsV2: FC = () => {
  const workspace = useWorkspace();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();

  const { data: audience, isLoading } = useGetAudienceQuery({
    workspaceId: workspace.id,
    audienceId: id,
  });

  const { data: datasources, isLoading: isDatasourcesLoading } = useGetDataSourcesByIdsQuery({
    workspaceId: workspace.id,
    datasourceIds: audience?.datasourceIds || [],
  });

  const isWorkspaceAdmin = useWorkspaceAdmin();

  const breadcrumbs = useMemo<BreadcrumbItem[]>(
    () => [
      {
        label: 'audiences:title',
        path: RouteConstants.audiences,
      },
      ...(audience?.name
        ? [
            {
              label: audience.name,
              path: RouteConstants.audiences + '/' + id,
            },
          ]
        : []),
    ],
    [audience?.name, id]
  );

  const handleConnectToCustomDestinationClick = () => {
    history.push(`${RouteConstants.createCustomDestination}?audienceId=${id}`);
  };

  return (
    <ErrorBoundary>
      <PageLayout className='overflow-x-auto grid grid-cols-1 w-full'>
        <Loadable isLoading={isLoading || isDatasourcesLoading}>
          <header className='flex flex-col gap-4 border-b border-secondary-border pb-4'>
            <Breadcrumbs items={breadcrumbs} />
            <AudienceDetailsHeader />
          </header>
          <div className='pt-8'>
            <div className='flex items-start justify-between gap-4 border-b border-secondary-border pb-6'>
              <section className='flex min-w-0 flex-col gap-1'>
                <h1 className='text-2xl font-semibold leading-8 text-primary-900'>{t('audience_details:destinations.call_to_action')}</h1>
                <h3 className='text-tertiary-600'>{t('audience_details:destinations.call_to_action_subtitle')}</h3>
              </section>
              <div className='flex shrink-0 gap-2'>
                <Button onClick={handleConnectToCustomDestinationClick} disabled={!isWorkspaceAdmin} className='mt-2 h-10 whitespace-nowrap'>
                  {t('audience_details:destinations.connect')}
                </Button>
              </div>
            </div>
            <AudienceDetailsSources datasources={datasources} />
            <AudienceDetailsRules audience={audience} datasources={datasources} />
            <AudienceDetailsDestinations audience={audience} />
          </div>
        </Loadable>
      </PageLayout>
    </ErrorBoundary>
  );
};
